// Outlet containers for the dashboard's container map / health widgets.

import { DEMO_CONTAINERS, DEMO_SENSORS, round, siteById, type DemoContainer } from '../core';
import type { ContainerMapItem } from '../../types/dashboard.type';
import { ContainerStatus } from '../../types/container.type';
import { jitter, materialName } from './helpers';
import { allPickupOrders } from './pickupOrders';

/** Bulk density used to express bin volume as a kg capacity (fill levels are kg). */
const KG_PER_LITRE = 0.25;

/** Wood-pallet cage at the distribution centre: lid hinge being repaired. */
const MAINTENANCE_CODES = new Set(['RUH-DC10-C04']);
/** The Al Malqa cardboard bin's sensor reports a tilt (bin knocked by a forklift). */
const TILTED_SENSOR_N = 3;

export function containerCapacityKg(container: DemoContainer): number {
  return Math.round(container.capacity_litres * KG_PER_LITRE);
}

/** Containers with a pickup booked in the last two days (not yet collected). */
function openOrderContainerIds(): Set<string> {
  const since = Date.now() - 2 * 86_400_000;
  return new Set(
    allPickupOrders()
      .filter((o) => (o.status === 'pending' || o.status === 'in_transit') && Date.parse(o.created_at) >= since)
      .map((o) => o.container_id),
  );
}

function statusFor(container: DemoContainer, awaiting: Set<string>): ContainerStatus {
  if (MAINTENANCE_CODES.has(container.code)) return ContainerStatus.MAINTENANCE;
  if (container.fill_level >= 80) {
    // Full bins: a pickup is already booked for some, the rest are ready to book.
    return awaiting.has(container.id) ? ContainerStatus.AWAITING_PICKUP : ContainerStatus.READY_FOR_PICKUP;
  }
  if (container.fill_level < 25) return ContainerStatus.EMPTY;
  return ContainerStatus.PENDING;
}

/** Every outlet container with position, fill (kg), capacity (kg) and sensor alarms. */
export function outletContainers(): ContainerMapItem[] {
  const awaiting = openOrderContainerIds();
  const perSite = new Map<string, number>();
  return DEMO_CONTAINERS.map((container) => {
    const site = siteById(container.site_id);
    const index = perSite.get(container.site_id) ?? 0;
    perSite.set(container.site_id, index + 1);
    const { dLat, dLng } = jitter(index);
    const capacity = containerCapacityKg(container);
    const sensor = container.sensor_id ? DEMO_SENSORS.find((s) => s.id === container.sensor_id) : undefined;
    const sensorN = sensor ? Number.parseInt(sensor.id.slice(-6), 16) : 0;
    const online = sensor ? sensor.status !== 'offline' : false;
    return {
      container_id: container.id,
      container_name: `${container.code} · ${materialName(container.material_id)}`,
      latitude: site ? round(site.lat + dLat, 6) : null,
      longitude: site ? round(site.lng + dLng, 6) : null,
      status: statusFor(container, awaiting),
      fill_level: round((capacity * container.fill_level) / 100, 1),
      capacity,
      capacity_unit: 'kg',
      alarms: sensor
        ? {
            full: online ? container.fill_level >= 90 : null,
            fire: online ? false : null,
            tilt: online ? sensorN === TILTED_SENSOR_N : null,
            battery: sensor.status === 'low_battery',
          }
        : { full: null, fire: null, tilt: null, battery: null },
      material_type_id: container.material_id,
    };
  });
}

/** Fill sensors report every two hours. */
const SENSOR_REPORTS_PER_DAY = 12;

/**
 * Fill-level reports a site's sensors sent between two instants (never past now).
 * Offline sensors stopped reporting ~30-50 h ago, so they count until then.
 */
export function fillReportsForSite(siteId: string, fromMs: number, toMs: number): number {
  const until = Math.min(toMs, Date.now());
  if (until <= fromMs) return 0;
  let total = 0;
  for (const sensor of DEMO_SENSORS) {
    if (sensor.site_id !== siteId) continue;
    const end = sensor.status === 'offline' ? Math.min(until, Date.parse(sensor.last_seen_at)) : until;
    if (end <= fromMs) continue;
    total += Math.floor(((end - fromMs) / 86_400_000) * SENSOR_REPORTS_PER_DAY);
  }
  return total;
}

/** UTC bounds of month 'YYYY-MM'. */
export function monthBounds(key: string): { from: number; to: number } {
  const [y, m] = key.split('-').map(Number);
  return { from: Date.UTC(y, m - 1, 1), to: Date.UTC(y, m, 1) - 1 };
}
